import { BaseQueryApi, BaseQueryFn, createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { enqueueSnackbar } from 'notistack';
import { logoutSuccess } from '@store/authSlice';
import { asyncDebounce } from '@utils/asyncDebounce';
import { REFRESH_TOKEN_URL, USER_ACCESS_TOKEN, USER_REFRESH_TOKEN } from '@constants/auth';
import { tokenStorage } from '@utils/tokenStorage';

// Debounce verification types
export interface EmailVerificationResult {
  email: string;
  result: string;
  reason: string;
  is_valid: boolean;
  is_disposable: boolean;
  is_role: boolean;
  free_email: boolean;
}

export interface DebounceVerificationRequest {
  emails: string[];
  level?: 'simple' | 'standard' | 'premium';
}

export interface DebounceVerificationResult {
  success: boolean;
  message: string;
  summary: {
    total: number;
    valid: number;
    invalid: number;
    validity_rate: number;
  };
  results: EmailVerificationResult[];
}

export interface DebouncePricingLevel {
  name: string;
  description: string;
  cost_per_email: number;
  tokens_per_100: number;
}

export interface DebouncePricingInfo {
  levels: {
    simple: DebouncePricingLevel;
    standard: DebouncePricingLevel;
    premium: DebouncePricingLevel;
  };
  recommended: string;
}

export interface TokenCostsInfo {
  costs: Record<string, number>;
  warmup_free: boolean;
  subscription_status: string;
  debounce_pricing: DebouncePricingInfo;
}

// City Search Types
export interface CitySearchResult {
  city: string;
  state: string;
  state_name?: string;
  lat: number;
  lng: number;
  population: number;
  full_name?: string;
}

export interface USState {
  code: string;
  name: string;
}

export interface ScraperSource {
  id: string;
  name: string;
  description: string;
}

export interface ScraperCategory {
  id: string;
  name: string;
  description: string;
  icon: string;
  sources: ScraperSource[];
}

export interface HygieneFilter {
  id: string;
  name: string;
  description: string;
  default: boolean;
  category: string;
  blocked_extensions?: string[];
  blocked_keywords?: string[];
  blocked_domains?: string[];
  blocked_patterns?: string[];
  custom_input?: boolean;
}

// Nearby Cities Types
export interface NearbyCity {
  city: string;
  state: string;
  state_name?: string;
  lat: number;
  lng: number;
  population: number;
  distance: number;
  is_primary?: boolean;
  leads_quota?: number;
}

// Lead Cache Types
export interface CacheAvailabilityRequest {
  keyword: string;
  location: string;
  count?: number;
}

export interface CacheLeadStats {
  total: number;
  with_email: {
    count: number;
    percent: number;
  };
  with_phone: {
    count: number;
    percent: number;
  };
  with_website?: {
    count: number;
    percent: number;
  };
  freshness: {
    fresh_7_days: number;
    recent_30_days: number;
    older: number;
  };
  average_age_days?: number;
}

// Lead preview for cache modal display
export interface CacheLeadPreview {
  id: string;
  business_name: string;
  email?: string;
  phone?: string;
  city?: string;
  state?: string;
}

export interface CachePricing {
  cached_only: { count: number; cost: number };
  fresh_only: { count: number; cost: number };
  hybrid: { cached_count: number; fresh_count: number; total_cost: number };
}

export interface CacheAvailabilityResponse {
  cached_count: number;
  requested_count: number;
  has_enough: boolean;
  stats: CacheLeadStats;
  pricing: CachePricing;
  lead_ids: string[];
  lead_previews?: CacheLeadPreview[];
}

export interface DeliverCachedLeadsRequest {
  lead_ids: string[];
  campaign_id: string;
}

export interface DeliverCachedLeadsResponse {
  success: boolean;
  delivered_count: number;
  total_cost: number;
  message: string;
}

export interface CacheStatsResponse {
  total_leads: number;
  with_real_email: number;
  with_phone: number;
  recent_7_days: number;
  by_source: Record<string, number>;
}

export interface CachePricingResponse {
  cached_lead_price: number;
  fresh_lead_price: number;
  description: {
    cached: string;
    fresh: string;
  };
}

export interface NearbyCitiesResponse {
  center: {
    city: string;
    state: string;
    lat: number;
    lng: number;
    population: number;
  };
  radius_miles: number;
  total_cities: number;
  cities: NearbyCity[];
}

export interface NearbyCitiesRequest {
  city: string;
  state: string;
  radius: number;
  limit?: number;
  leads_count?: number;
}

const baseQuery = fetchBaseQuery({
  baseUrl: import.meta.env.VITE_API_URL || 'http://localhost:8000/api',
  credentials: 'include',
  prepareHeaders: headers => {
    const token = tokenStorage.getToken(USER_ACCESS_TOKEN);
    if (token) {
      headers.set('Authorization', `Bearer ${token}`);
    }
    headers.set('Content-Type', 'application/json');
    return headers;
  },
});

const refreshTokenDebounced = asyncDebounce(
  async (api: BaseQueryApi, extraOptions: {}) =>
    baseQuery(
      {
        url: REFRESH_TOKEN_URL,
        method: 'POST',
        body: { refresh: tokenStorage.getToken(USER_REFRESH_TOKEN) } as any,
      },
      api,
      extraOptions,
    ),
  5000,
  { leading: true, trailing: false },
);

const baseQueryWithRefreshToken: BaseQueryFn = async (args, api, extraOptions) => {
  let result = await baseQuery(args, api, extraOptions);

  if (result?.error) {
    if (result.error.status === 401 || (result.error as any).originalStatus === 401) {
      const refreshResult = await refreshTokenDebounced(api, extraOptions);
      const data = refreshResult?.data as any | undefined;
      if (data) {
        tokenStorage.setToken(USER_ACCESS_TOKEN, data.access);
        tokenStorage.setToken(USER_REFRESH_TOKEN, data.refresh);
        result = await baseQuery(args, api, extraOptions);
      } else {
        tokenStorage.cleanup();
        api.dispatch(logoutSuccess());
      }
    } else if (result.error.status === 500) {
      enqueueSnackbar('Something went wrong. Please try again later.', { variant: 'error', preventDuplicate: true });
    } else if (result.error.status === 503) {
      enqueueSnackbar('Service temporarily unavailable. Please try again shortly.', {
        variant: 'warning',
        preventDuplicate: true,
      });
    } else if (result.error.status === 'FETCH_ERROR') {
      enqueueSnackbar('Connection lost. Please check your network.', { variant: 'error', preventDuplicate: true });
    }
  }

  return result;
};

export const djangoApi = createApi({
  reducerPath: 'djangoApi',
  baseQuery: baseQueryWithRefreshToken,
  tagTypes: [
    'User',
    'Organization',
    'ScrapeJob',
    'ScrapeTemplate',
    'Campaign',
    'Lead',
    'Marketplace',
    'Billing',
    'TokenBalance',
  ],
  endpoints: builder => ({
    // Authentication
    login: builder.mutation({
      query: credentials => ({
        url: '/auth/login/',
        method: 'POST',
        body: credentials,
      }),
    }),
    register: builder.mutation({
      query: userData => ({
        url: '/auth/register/',
        method: 'POST',
        body: userData,
      }),
    }),
    refreshToken: builder.mutation({
      query: refreshToken => ({
        url: '/auth/refresh/',
        method: 'POST',
        body: { refresh: refreshToken },
      }),
    }),
    verifyEmail: builder.query({
      query: token => `/auth/verify-email/?token=${token}`,
    }),

    // Scrape Jobs
    getScrapeJobs: builder.query({
      query: params => ({
        url: '/jobs/',
        params,
      }),
      providesTags: ['ScrapeJob'],
    }),
    createScrapeJob: builder.mutation({
      query: jobData => ({
        url: '/jobs/',
        method: 'POST',
        body: jobData,
      }),
      invalidatesTags: ['ScrapeJob'],
    }),
    getScrapeJobDetails: builder.query({
      query: id => `/jobs/${id}/`,
      providesTags: ['ScrapeJob'],
    }),
    cancelScrapeJob: builder.mutation({
      query: id => ({
        url: `/jobs/${id}/cancel/`,
        method: 'POST',
      }),
      invalidatesTags: ['ScrapeJob'],
    }),
    getScrapeJobResults: builder.query({
      query: ({ id, page = 1, pageSize = 50 }) => `/jobs/${id}/results/?page=${page}&page_size=${pageSize}`,
    }),
    exportScrapeJob: builder.query({
      query: ({ id, format = 'csv' }) => `/jobs/${id}/export/?format=${format}`,
    }),

    // Scrape Templates
    getScrapeTemplates: builder.query({
      query: params => ({
        url: '/templates/',
        params,
      }),
      providesTags: ['ScrapeTemplate'],
    }),
    createScrapeTemplate: builder.mutation({
      query: templateData => ({
        url: '/templates/',
        method: 'POST',
        body: templateData,
      }),
      invalidatesTags: ['ScrapeTemplate'],
    }),
    updateScrapeTemplate: builder.mutation({
      query: ({ id, ...data }) => ({
        url: `/templates/${id}/`,
        method: 'PATCH',
        body: data,
      }),
      invalidatesTags: ['ScrapeTemplate'],
    }),

    // Campaigns
    getCampaigns: builder.query({
      query: params => ({
        url: '/campaigns/',
        params,
      }),
      providesTags: ['Campaign'],
    }),
    createCampaign: builder.mutation({
      query: campaignData => ({
        url: '/campaigns/',
        method: 'POST',
        body: campaignData,
      }),
      invalidatesTags: ['Campaign'],
    }),
    getCampaignDetails: builder.query({
      query: id => `/campaigns/${id}/`,
      providesTags: ['Campaign'],
    }),
    updateCampaign: builder.mutation({
      query: ({ id, ...data }) => ({
        url: `/campaigns/${id}/`,
        method: 'PATCH',
        body: data,
      }),
      invalidatesTags: ['Campaign'],
    }),
    deleteCampaign: builder.mutation({
      query: id => ({
        url: `/campaigns/${id}/`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Campaign'],
    }),

    // Leads
    getLeads: builder.query({
      query: params => ({
        url: '/leads/',
        params,
      }),
      providesTags: ['Lead'],
    }),
    createLead: builder.mutation({
      query: leadData => ({
        url: '/leads/',
        method: 'POST',
        body: leadData,
      }),
      invalidatesTags: ['Lead'],
    }),
    importLeads: builder.mutation({
      query: formData => ({
        url: '/leads/import/',
        method: 'POST',
        body: formData,
      }),
      invalidatesTags: ['Lead'],
    }),
    exportLeads: builder.query({
      query: ({ campaignId, format = 'csv' }) => `/leads/export/?campaign_id=${campaignId}&format=${format}`,
    }),

    // Marketplace
    getMarketplaceListings: builder.query({
      query: params => ({
        url: '/marketplace/',
        params,
      }),
      providesTags: ['Marketplace'],
    }),
    createMarketplaceListing: builder.mutation({
      query: listingData => ({
        url: '/marketplace/',
        method: 'POST',
        body: listingData,
      }),
      invalidatesTags: ['Marketplace'],
    }),
    purchaseLeads: builder.mutation({
      query: purchaseData => ({
        url: '/marketplace/purchase/',
        method: 'POST',
        body: purchaseData,
      }),
      invalidatesTags: ['Marketplace', 'TokenBalance'],
    }),

    // Billing & Tokens
    getTokenBalance: builder.query({
      query: () => '/billing/balance/',
      providesTags: ['TokenBalance'],
    }),
    getTransactionHistory: builder.query({
      query: params => ({
        url: '/billing/transactions/',
        params,
      }),
      providesTags: ['Billing'],
    }),
    createCheckoutSession: builder.mutation({
      query: checkoutData => ({
        url: '/billing/create-checkout/',
        method: 'POST',
        body: checkoutData,
      }),
    }),
    handleStripeWebhook: builder.mutation({
      query: webhookData => ({
        url: '/billing/webhook/',
        method: 'POST',
        body: webhookData,
      }),
    }),
    getInvoiceDetails: builder.query<any, { id?: string }>({
      query: ({ id }) => `/billing/invoice/${id}/`,
    }),

    // Email Verification (Debounce)
    verifyEmailsDebounce: builder.mutation<DebounceVerificationResult, DebounceVerificationRequest>({
      query: data => ({
        url: '/billing/verify-emails/',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['TokenBalance'],
    }),
    getDebouncePricing: builder.query<DebouncePricingInfo, void>({
      query: () => '/billing/debounce-pricing/',
    }),
    getTokenCosts: builder.query<TokenCostsInfo, void>({
      query: () => '/billing/token-costs/',
      providesTags: ['TokenBalance'],
    }),

    // Organizations
    getOrganization: builder.query({
      query: () => '/organizations/current/',
      providesTags: ['Organization'],
    }),
    updateOrganization: builder.mutation({
      query: data => ({
        url: '/organizations/current/',
        method: 'PATCH',
        body: data,
      }),
      invalidatesTags: ['Organization'],
    }),
    inviteTeamMember: builder.mutation({
      query: inviteData => ({
        url: '/organizations/invite/',
        method: 'POST',
        body: inviteData,
      }),
      invalidatesTags: ['Organization'],
    }),

    // Hybrid Scraping Engine
    startHybridScrape: builder.mutation({
      query: scrapeData => ({
        url: '/hybrid/scrape/',
        method: 'POST',
        body: scrapeData,
      }),
      invalidatesTags: ['ScrapeJob'],
    }),
    getAvailableSpiders: builder.query({
      query: () => '/hybrid/spiders/',
    }),
    getEngineStats: builder.query({
      query: () => '/hybrid/stats/',
    }),

    // User Profile
    getUserProfile: builder.query({
      query: () => '/auth/profile/',
      providesTags: ['User'],
    }),
    updateUserProfile: builder.mutation({
      query: profileData => ({
        url: '/auth/profile/',
        method: 'PATCH',
        body: profileData,
      }),
      invalidatesTags: ['User'],
    }),

    // Campaign Wizard - Location & Filters
    searchCities: builder.query<CitySearchResult[], { q: string; state?: string; limit?: number }>({
      query: ({ q, state, limit = 10 }) => ({
        url: '/cities/search/',
        params: { q, state, limit },
      }),
    }),
    getCityByZip: builder.query<CitySearchResult, { zip: string }>({
      query: ({ zip }) => ({
        url: '/cities/zip/',
        params: { zip },
      }),
    }),
    getUsStates: builder.query<USState[], void>({
      query: () => '/us-states/',
    }),
    getScraperCategories: builder.query<ScraperCategory[], void>({
      query: () => '/scraper-categories/',
    }),
    getHygieneFilters: builder.query<HygieneFilter[], void>({
      query: () => '/hygiene-filters/',
    }),

    // Nearby Cities - for radius-based location expansion
    getNearbyCities: builder.query<NearbyCitiesResponse, NearbyCitiesRequest>({
      query: ({ city, state, radius, limit = 50, leads_count }) => ({
        url: '/cities/nearby/',
        params: { city, state, radius, limit, ...(leads_count ? { leads_count } : {}) },
      }),
    }),

    // Lead Cache API - for smart campaign creation with cached leads
    checkCacheAvailability: builder.query<CacheAvailabilityResponse, CacheAvailabilityRequest>({
      query: ({ keyword, location, count = 1000 }) => ({
        url: '/cache/check/',
        params: { keyword, location, count },
      }),
    }),
    deliverCachedLeads: builder.mutation<DeliverCachedLeadsResponse, DeliverCachedLeadsRequest>({
      query: data => ({
        url: '/cache/deliver/',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['Lead', 'TokenBalance'],
    }),
    getCacheStats: builder.query<CacheStatsResponse, void>({
      query: () => '/cache/stats/',
    }),
    getCachePricing: builder.query<CachePricingResponse, void>({
      query: () => '/cache/pricing/',
    }),
  }),
});

// Export hooks for usage in functional components
export const {
  // Authentication
  useLoginMutation,
  useRegisterMutation,
  useRefreshTokenMutation,
  useVerifyEmailQuery,

  // Scrape Jobs
  useGetScrapeJobsQuery,
  useCreateScrapeJobMutation,
  useGetScrapeJobDetailsQuery,
  useCancelScrapeJobMutation,
  useGetScrapeJobResultsQuery,
  useExportScrapeJobQuery,

  // Scrape Templates
  useGetScrapeTemplatesQuery,
  useCreateScrapeTemplateMutation,
  useUpdateScrapeTemplateMutation,

  // Campaigns
  useGetCampaignsQuery,
  useCreateCampaignMutation,
  useGetCampaignDetailsQuery,
  useUpdateCampaignMutation,
  useDeleteCampaignMutation,

  // Leads
  useGetLeadsQuery,
  useCreateLeadMutation,
  useImportLeadsMutation,
  useExportLeadsQuery,

  // Marketplace
  useGetMarketplaceListingsQuery,
  useCreateMarketplaceListingMutation,
  usePurchaseLeadsMutation,

  // Billing
  useGetTokenBalanceQuery,
  useGetTransactionHistoryQuery,
  useCreateCheckoutSessionMutation,
  useGetInvoiceDetailsQuery,

  // Email Verification (Debounce)
  useVerifyEmailsDebounceMutation,
  useGetDebouncePricingQuery,
  useGetTokenCostsQuery,

  // Organizations
  useGetOrganizationQuery,
  useUpdateOrganizationMutation,
  useInviteTeamMemberMutation,

  // Hybrid Engine
  useStartHybridScrapeMutation,
  useGetAvailableSpidersQuery,
  useGetEngineStatsQuery,

  // User Profile
  useGetUserProfileQuery,
  useUpdateUserProfileMutation,

  // Campaign Wizard - Location & Filters
  useSearchCitiesQuery,
  useLazySearchCitiesQuery,
  useGetCityByZipQuery,
  useGetUsStatesQuery,
  useGetScraperCategoriesQuery,
  useGetHygieneFiltersQuery,

  // Nearby Cities - for radius-based location expansion
  useGetNearbyCitiesQuery,
  useLazyGetNearbyCitiesQuery,

  // Lead Cache - for smart campaign creation
  useCheckCacheAvailabilityQuery,
  useLazyCheckCacheAvailabilityQuery,
  useDeliverCachedLeadsMutation,
  useGetCacheStatsQuery,
  useGetCachePricingQuery,
} = djangoApi;
